import { Component, EventEmitter, Input, OnInit, OnChanges, Output, SimpleChanges } from "@angular/core";
import { FormBuilder, FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import moment from "moment";
import { Receivable } from "src/app/models/receivable.model";
import { MasterService } from "src/app/services/master.service";
import { MessageService } from "src/app/services/message.service";
import { formatCurrency } from "src/app/utils/format";
import { clean } from "src/app/utils/numeric";

@Component({
    selector: 'write-off-modal',
    templateUrl: './write-off-modal.component.html',
    styleUrls: ['./write-off-modal.component.scss'],
    standalone: false
})

export class WriteOffModalComponent implements OnInit, OnChanges {

    /** The latest receivable meta associated with the sale to be written off. */
    @Input() receivable: Receivable | null = null

    /** In order to have multiple components on a single page (tabs, etc...) we need a key to uniquely identify each one. */
    @Input() key: string | null = null

    /** The current user selected language code. */
    @Input() language: string = 'EN'

    /** A list of language translations for use in the UI. */
    @Input() words = {}

    /** Indicates to the callee that the user chose to proceed with the write off and it was successful. */
    @Output() writtenOff: EventEmitter<Object> = new EventEmitter()
    
    /** Form controls related to collecting write-off information from the user. */
    protected form: FormGroup;

    /**
     * Helper method to consistently get the list of write-off charges as an Array.
     */
    get charges() {
        return this.form.controls['charges'] as FormArray;
    }

    constructor(private master: MasterService, private ms: MessageService, private fb: FormBuilder) {
        this.form = this.fb.group({
            id: new FormControl<number>(0, [Validators.required, Validators.min(1)]),
            when: new FormControl<string>(moment().format('YYYY-MM-DD'), [Validators.required]),
            charges: this.fb.array([])
        })

        this.add(null)
    }

    ngOnInit(): void {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }
        
        if (changes.receivable) {
            this.form.get('id').setValue(this.receivable.id)
        }
    }

    /**
     * Remove a write-off charge from the form array
     * @param index The index of the invite to remove
     */
    public discard = (index: number): void => {
        if ((index < 0) || (index > this.charges.length - 1)) {
            return
        }

        this.charges.removeAt(index)
    }

    /**
     * Add a new write-off charge row to the form array
     * @param e Event that triggerd this method, if any
     */
    public add = (e): void => {
        const charge = this.fb.group({
            amount: new FormControl<string>(formatCurrency(0), [Validators.required]),
            type: new FormControl<string>('other', []),
            vendor: new FormControl<number>(null, [])
        })

        this.charges.push(charge)
    }

    /**
     * The write-off process was initiated by the user, notify the callee once complete.
     * @param {Event} e The event that triggered the write-off
     */
    protected writeOff = (e: Event) => {
        if (this.form.invalid) {
            return
        }

        if (!this.receivable || !this.receivable.saleId || (this.receivable.saleId <= 0) || 
            (!this.receivable.bhph) || (this.receivable.status.toLowerCase() !== 'sold')) {
            return
        }

        const meta = {
            writeOffDate: this.form.get('when').value,
            charges: []
        }

        for (const charge of this.charges.controls) {
            const amount = clean(charge.get('amount').value)
            if (!amount || (amount <= 0)) {
                continue
            }

            meta.charges.push({
                amount: amount,
                type: charge.get('type').value,
                vendorId: parseInt(charge.get('vendor').value)
            })
        }

        this.master.patch(`sales/${this.receivable.saleId}/write-off`, meta, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.writtenOff.emit(meta)
        })
    }
}
